<script setup lang="ts">
import { OBJECT } from '@pkg/config/sanity/schemas';
import { convertGeneralLinkToLinkType } from '@pkg/link';
import { TileSize } from '@pkg/sanity-utils/types/tile';
import type { Image } from '@sanity/types';
import type { Ref } from 'vue';
import { resolveComponent } from 'vue';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import SectionImages from '../../outer/Section/SectionImages.vue';
import type { TileProps } from './BaseTile/BaseTile.vue';
import TilesGridTileVue, { type TilesGridTileProps } from './TilesGridTile.vue';
import SolutionsTile from './SolutionsTile.vue';

type Tile = TilesGridTileProps['tile'];

interface Props {
  tiles: Array<Tile>,
  type: TilesGridTileProps['type'],
  variant: TileProps['variant'],
  size?: TilesGridTileProps['size'],
  image?: Image;
  subImage?: Image;
}

const props = withDefaults(defineProps<Props>(), {
  size: TileSize.MEDIUM,
  image: undefined,
  subImage: undefined,
});

const getLink = (tile: Tile, link: NonNullable<Tile['link']>) => {
  const realLink = link.link;

  if (realLink && 'linkType' in realLink) {
    return convertGeneralLinkToLinkType(realLink, link.title, `${tile._key}_link`);
  }

  return realLink;
};

const hoveredTileIndex: Ref<number> = ref(0);
const handleActiveTile = (tileIndex: number) => {
  hoveredTileIndex.value = tileIndex;
};

/** Host app registers BaseImage from @pkg/ui. */
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <div class="flex gap-[24px] flex-col desk:flex-row">
    <div v-if="props.variant === 'default' && tiles && tiles[0]?.image?.asset" class="c-tiles__image-container">
      <template v-for="(tile, index) in props.tiles" :key="tile._key">
        <SectionImages
          v-if="tile.image != null"
          :image="tile.image"
          :sub-image="tile.subImage"
          alignment="left"
          loading="lazy"
          class="flex-shrink-0 mx-auto tablet-wide:mx-[unset] c-tile__image"
          :class="{'c-tile__image--active': index === hoveredTileIndex}"
        />
      </template>
    </div>
    <div
      :class="['c-tiles-grid', {
        'c-tiles-grid--large': size === TileSize.LARGE,
      }]"
    >
      <div v-for="(tile, index) in props.tiles" :key="tile._key" class="c-tiles-grid__item">
        <template v-if="props.type === OBJECT.MODULAR_INNER_BLOCK_TILES">
          <CustomLink v-if="tile.link != null" :link="getLink(tile, tile.link)" :title="tile.link.title">
            <TilesGridTileVue :tile="tile" :variant="props.variant" :type="props.type" :size="props.size" />
          </CustomLink>
          <TilesGridTileVue v-else :tile="tile" :variant="props.variant" :type="props.type" :size="props.size" />
        </template>
        <SolutionsTile
          v-else-if="tile.link"
          :title="tile.title"
          :pre-title="tile.preTitle"
          :variant="props.variant as TileProps['variant']"
          :size="props.size"
          class="h-full flex-1"
          :link-label="props.size === TileSize.MEDIUM ? tile.link?.label : undefined"
          :link="getLink(tile, tile.link)"
          :link-text="props.size === TileSize.MEDIUM ? tile.link?.title : undefined"
          :description="tile.content"
          image-in-content
          @mouseenter="tile.image && handleActiveTile(index)"
          @mouseleave="tile.image && handleActiveTile(0)"
        >
          <template v-if="props.variant === 'center' && tile.image != null" #image>
            <component
              :is="BaseImage"
              :image="tile.image"
              :height="160"
              :width="160"
              class="w-[120px] h-[120px] tablet-wide:w-[160px] tablet-wide:h-[160px] aspect-square object-contain"
            />
          </template>
          <UiButton v-if="props.size === TileSize.LARGE">
            {{ tile.link?.title }}
          </UiButton>
        </SolutionsTile>
      </div>
    </div>
  </div>
</template>

<style scoped>
.c-tiles-grid {
  width: 100%;
  --gap: 0.75rem;
  --rows: 1;

  @screen tablet-wide {
    --gap: 1.5rem;
    --rows: 2;
  }

  @screen desk {
    --rows: 3;

    @container (max-width: theme('screens.tablet-wide')) {
      --rows: 2;
    }
  }

  &.c-tiles-grid--large {
    --rows: 1;

    @screen tablet-wide {
      --rows: 2;
    }
  }

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  gap: var(--gap);

  &__item {
    /*
      Calculate width of each item based on the number of rows and the gap between them.
    */
    flex: 0 0 calc(
      calc(100% / var(--rows-override, var(--rows))) -
      calc(calc(var(--gap) * calc(var(--rows-override, var(--rows)) - 1)) / var(--rows-override, var(--rows))));

    align-self: stretch;
    justify-self: stretch;
    display: flex;
    flex-direction: column;

    & :deep(a) {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
}

.c-tiles__image-container {
  --tile-image-container: 320px;

  @screen desk {
    --tile-image-container: 588px;
  }

  max-width: var(--tile-image-container);
  width: 100%;
  height: var(--tile-image-container);
  position: relative;
  margin-left: auto;
  margin-right: auto;

  & > :deep(.c-section-images) {
    position: absolute;
    top: 0;
    left: 0;
  }

  & > :deep(.c-section-images__image) {
    position: absolute;
    transition: opacity 0.3s ease-in-out;
  }

  & > :deep(.c-section-images__image--main) {
    bottom: 0;
    left: 0;
    width: var(--main-image-size);
    max-width: var(--main-image-size);
    height: var(--main-image-size);
    max-height: var(--main-image-size);
  }

  & > :deep(.c-section-images__image--sub) {
    top: 0;
    right: 0;
    width: var(--sub-image-size);
    max-width: var(--sub-image-size);
    height: var(--sub-image-size);
    max-height: var(--sub-image-size);
  }
}

.c-tile__image {
  opacity: 0;
  transition: opacity 500ms ease-in-out;
}

.c-tile__image--active {
  opacity: 1;
  transition: opacity 500ms ease-in-out;
}
</style>
